import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Auth } from '@angular/fire/auth';

import { AuthService } from 'src/app/services/auth.service';
import { BackendService } from 'src/app/services/backend.service';
import { ToastService } from 'src/app/services/toast.service';

/**
 * Hinweis, solange die Mailadresse nicht bestätigt ist.
 *
 * **Warum nur ein Hinweis und keine Sperre.** Der Song ist sofort
 * freigeschaltet. Ihn hinter den Klick in einer Mail zu legen wäre genau die
 * Mauer, die mit 2.1 gefallen ist — im Moment der höchsten Kaufbereitschaft,
 * und wessen Mail im Spam landet, steht ohne sein Lied da. Die Bestätigung
 * braucht es für die Zustellbarkeit und für den Newsletter, nicht für die
 * Nutzung.
 *
 * Standalone wie die anderen Komponenten hier: er hängt an keinem Modul, wird
 * aber an mehreren Stellen gebraucht.
 */
@Component({
  standalone: true,
  selector: 'app-verify-hint',
  templateUrl: './verify-hint.component.html',
  styleUrls: ['./verify-hint.component.scss'],
  imports: [IonicModule, CommonModule],
})
export class VerifyHintComponent implements OnInit, OnDestroy {
  private fireAuth = inject(Auth);
  private authService = inject(AuthService);
  private backendService = inject(BackendService);

  show = false;
  sending = false;

  /**
   * Merker, dass hier eine Bestaetigung offen war.
   *
   * Ohne ihn liesse sich die Rueckkehr nicht von einem gewoehnlichen Aufruf
   * unterscheiden, und der Hinweis "bestätigt" kaeme bei jedem zweiten
   * Seitenaufbau. Je Konto, damit er nicht am naechsten Nutzer desselben
   * Geraets haengen bleibt.
   */
  private get pendingKey(): string {
    return `melowish_verify_pending:${this.fireAuth.currentUser?.uid ?? ''}`;
  }

  /**
   * Der Klick passiert woanders - im Mailprogramm, in einem anderen Tab.
   *
   * Zurueck in der App laeuft kein Lebenszyklus, der das bemerken wuerde:
   * Ionic haelt die Seite im Stapel, `ngOnInit` kommt kein zweites Mal. Das
   * Sichtbarwerden des Dokuments ist das Ereignis, das es wirklich gibt.
   */
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') void this.refresh();
  };

  async ngOnInit(): Promise<void> {
    document.addEventListener('visibilitychange', this.onVisible);
    await this.refresh();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  /**
   * Holt den Bestätigungsstand frisch vom Server.
   *
   * `emailVerified` am lokalen Nutzer altert: der Klick passiert in einem
   * anderen Tab oder auf einem anderen Gerät, und das Token hier weiß nichts
   * davon, bis es erneuert wird. Ohne `reload()` stünde der Hinweis noch
   * stundenlang da, obwohl längst bestätigt wurde.
   */
  private async refresh(): Promise<void> {
    const user = this.fireAuth.currentUser;

    if (!user || user.isAnonymous || !user.email) {
      this.show = false;
      return;
    }

    try {
      await user.reload();
    } catch {
      // Offline oder Token abgelaufen - dann gilt der letzte bekannte Stand.
    }

    const wasPending = this.readPending();
    this.show = !this.fireAuth.currentUser?.emailVerified;

    if (this.show) {
      this.writePending(true);
      return;
    }

    // Frisch bestätigt: jetzt darf die Einwilligung festgehalten werden. Das
    // Backend prüft dabei selbst, ob überhaupt eine angefragt war.
    void this.confirmConsent();

    // Nur wenn hier vorher etwas offen war. Sonst bekäme jeder, der die Seite
    // öffnet, die Meldung über etwas, das er längst erledigt hat.
    if (wasPending) {
      this.writePending(false);
      ToastService.makeToast('E-Mail-Adresse bestätigt.');
    }
  }

  private readPending(): boolean {
    try {
      return localStorage.getItem(this.pendingKey) === '1';
    } catch {
      return false;
    }
  }

  private writePending(pending: boolean): void {
    try {
      if (pending) localStorage.setItem(this.pendingKey, '1');
      else localStorage.removeItem(this.pendingKey);
    } catch {
      // Gesperrter Speicher - dann entfällt die Meldung, mehr nicht.
    }
  }

  private async confirmConsent(): Promise<void> {
    try {
      // Das Token trägt `email_verified` erst nach einer Erneuerung - sonst
      // weist das Backend die Bestätigung zu Recht ab.
      await this.authService.currentUser?.getIdToken(true);
      await this.backendService.confirmNewsletterConsent();
    } catch (e) {
      console.warn('Newsletter-Einwilligung noch nicht bestätigt', e);
    }
  }

  async resend(): Promise<void> {
    if (this.sending) return;
    this.sending = true;

    try {
      const { alreadyVerified } = await this.backendService.resendVerificationMail();

      if (alreadyVerified) {
        this.show = false;
        ToastService.makeToast('Deine E-Mail ist bereits bestätigt.');
        return;
      }

      ToastService.makeToast('Wir haben die Mail noch einmal verschickt.');
    } catch (e) {
      console.error('Bestätigungsmail nicht verschickbar', e);
      ToastService.makeToast(
        'Die Mail lässt sich gerade nicht verschicken. Versuch es später noch einmal.'
      );
    } finally {
      this.sending = false;
    }
  }
}
